import { parseFlow } from './flowParser';

export type CurrentStateVsmStatus = 'empty' | 'partial' | 'ready' | 'frozen';

export interface CurrentStateVsmInput {
  stream?: CurrentStateVsmStreamLike | null;
  sessionId?: string | null;
  flowText?: string | null;
  vsm?: VsmLike | null;
  spine?: unknown[] | null;
  shared?: {
    processIds: string[];
    steps: Array<{ stepId: string; processId: string; label: string; measurement?: VsmStepLike }>;
  };
}

export interface CurrentStateVsmStreamLike {
  id?: string;
  name?: string;
  vsmBaseline?: unknown;
  sequenceOverlay?: unknown;
  painLinks?: unknown[];
}

export interface VsmLike {
  processes?: VsmProcessLike[];
}

export interface VsmProcessLike {
  id?: string;
  label?: string;
  steps?: VsmStepLike[];
}

export interface VsmStepLike {
  statementId?: unknown;
  ct?: unknown;
  workTimeMinutes?: unknown;
  waitTimeMinutes?: unknown;
  wt?: unknown;
  ca?: unknown;
  waitReason?: unknown;
  waitReasonText?: unknown;
  waitCause?: unknown;
  delayReason?: unknown;
  reason?: unknown;
  notes?: unknown;
}

export interface CurrentStateVsmReadModel {
  status: CurrentStateVsmStatus;
  sessionId: string | null;
  processId: string | null;
  processLabel: string;
  flow: CurrentStateVsmFlowStep[];
  metrics: CurrentStateVsmMetrics;
  missingFacts: CurrentStateVsmMissingFact[];
  bottlenecks: CurrentStateVsmBottleneck[];
  hasBaseline: boolean;
  baselineSessionId: string | null;
  canFreezeBaseline: boolean;
  sourceVsm: VsmLike | null;
}

export interface CurrentStateVsmFlowStep {
  id: string;
  order: number;
  statementId: number | null;
  label: string;
  workTime: DurationFact;
  waitTime: DurationFact;
  quality: PercentFact;
  waitReason: string | null;
}

export interface CurrentStateVsmMetrics {
  leadTime: DurationMetric;
  workTime: DurationMetric;
  waitTime: DurationMetric;
  flowEfficiency: PercentMetric;
  quality: PercentMetric;
}

export type DurationMetric = KnownDurationMetric | MissingDurationMetric;
export type PercentMetric = KnownPercentMetric | MissingPercentMetric;
export type DurationFact = KnownDurationFact | MissingFactValue;
export type PercentFact = KnownPercentFact | MissingFactValue;

export interface KnownDurationMetric {
  status: 'known';
  kind: 'duration';
  seconds: number;
  display: string;
}

export interface MissingDurationMetric {
  status: 'missing';
  kind: 'duration';
  display: 'Missing';
  reason: string;
}

export interface KnownPercentMetric {
  status: 'known';
  kind: 'percent';
  percent: number;
  display: string;
}

export interface MissingPercentMetric {
  status: 'missing';
  kind: 'percent';
  display: 'Missing';
  reason: string;
}

export interface KnownDurationFact {
  status: 'known';
  kind: 'duration';
  seconds: number;
  display: string;
}

export interface KnownPercentFact {
  status: 'known';
  kind: 'percent';
  percent: number;
  display: string;
}

export interface MissingFactValue {
  status: 'missing';
  display: 'Missing';
}

export interface CurrentStateVsmMissingFact {
  kind: 'flow' | 'work-time' | 'wait-time' | 'wait-reason' | 'quality';
  stepId?: string;
  stepLabel?: string;
  message: string;
}

export interface CurrentStateVsmBottleneck {
  kind: 'wait' | 'elapsed-time' | 'quality' | 'pain';
  stepId?: string;
  stepLabel: string;
  reason: string;
  detail: string;
}

export interface CurrentStateVsmBaselineSnapshot {
  source: 'current-state-vsm-readout';
  sessionId: string | null;
  processId: string | null;
  processLabel: string;
  vsm?: VsmLike;
  currentStateVsm: {
    status: CurrentStateVsmStatus;
    flow: CurrentStateVsmFlowStep[];
    metrics: CurrentStateVsmMetrics;
    bottlenecks: CurrentStateVsmBottleneck[];
    missingFacts: CurrentStateVsmMissingFact[];
  };
}

interface FlowStatementLike {
  id: number;
  name: string;
}

interface SequenceStepLike {
  order: number;
  statementId: number | null;
  label: string;
}

interface StepTiming {
  workTime: DurationFact;
  waitTime: DurationFact;
  quality: PercentFact;
  waitReason: string | null;
}

const EMPTY_METRICS: CurrentStateVsmMetrics = {
  leadTime: missingDuration('Capture work and wait times to calculate lead time.'),
  workTime: missingDuration('Capture work time for each step.'),
  waitTime: missingDuration('Capture wait time for each step.'),
  flowEfficiency: missingPercent('Capture work and wait times to calculate flow efficiency.'),
  quality: missingPercent('Capture quality for each step.'),
};

export function deriveCurrentStateVsmReadModel(
  input: CurrentStateVsmInput,
): CurrentStateVsmReadModel {
  const parsedStatements = parseFlow(input.flowText || '').statements.map((statement) => ({
    id: statement.id,
    name: statement.name,
  }));
  const statementById = new Map(parsedStatements.map((statement) => [statement.id, statement]));
  const selectedProcess = selectProcess(input.vsm);
  const vsmSteps = selectedProcess?.steps ?? [];
  const vsmStepByStatement = new Map<number, VsmStepLike>();

  for (const step of vsmSteps) {
    const statementId = toNumber(step.statementId);
    if (statementId != null && !vsmStepByStatement.has(statementId)) {
      vsmStepByStatement.set(statementId, step);
    }
  }

  const sequenceSteps = extractSequenceSteps(input.stream?.sequenceOverlay);
  const flow = deriveFlow({
    sequenceSteps,
    parsedStatements,
    statementById,
    vsmSteps,
    vsmStepByStatement,
    spine: input.spine,
  });
  const flowWithFacts = input.shared ? input.shared.steps.map((step, order) => ({
    id: step.stepId, statementId: null, order: order + 1, label: step.label,
    ...deriveStepTiming(step.measurement),
  })) : flow.map((step) => {
    const vsmStep =
      step.statementId == null ? undefined : vsmStepByStatement.get(step.statementId);
    return {
      ...step,
      ...deriveStepTiming(vsmStep),
    };
  });

  const missingFacts = deriveMissingFacts(flowWithFacts);
  const metrics = deriveMetrics(flowWithFacts);
  const bottlenecks = deriveBottlenecks(flowWithFacts, input.stream?.painLinks);
  const hasBaseline = Boolean(input.stream?.vsmBaseline);
  const status = deriveStatus(flowWithFacts, missingFacts, hasBaseline);

  return {
    status,
    sessionId: input.sessionId ?? null,
    processId: input.shared ? null : selectedProcess?.id ?? null,
    processLabel: humanizeLabel(selectedProcess?.label || input.stream?.name || 'Current flow'),
    flow: flowWithFacts,
    metrics,
    missingFacts:
      flowWithFacts.length > 0
        ? missingFacts
        : [
            {
              kind: 'flow',
              message: 'No current-state flow has been captured yet.',
            },
          ],
    bottlenecks,
    hasBaseline,
    baselineSessionId: getBaselineSessionId(input.stream?.vsmBaseline),
    canFreezeBaseline: flowWithFacts.length > 0 && hasKnownMetric(metrics),
    sourceVsm: input.shared ? null : input.vsm ?? null,
  };
}

export function createCurrentStateVsmBaselineSnapshot(
  readModel: CurrentStateVsmReadModel,
): CurrentStateVsmBaselineSnapshot {
  return {
    source: 'current-state-vsm-readout',
    sessionId: readModel.sessionId,
    processId: readModel.processId,
    processLabel: readModel.processLabel,
    ...(readModel.sourceVsm ? { vsm: readModel.sourceVsm } : {}),
    currentStateVsm: {
      status: readModel.status,
      flow: readModel.flow,
      metrics: readModel.metrics,
      bottlenecks: readModel.bottlenecks,
      missingFacts: readModel.missingFacts,
    },
  };
}

function deriveFlow(input: {
  sequenceSteps: SequenceStepLike[];
  parsedStatements: FlowStatementLike[];
  statementById: Map<number, FlowStatementLike>;
  vsmSteps: VsmStepLike[];
  vsmStepByStatement: Map<number, VsmStepLike>;
  spine?: unknown[] | null;
}): Array<Omit<CurrentStateVsmFlowStep, keyof StepTiming>> {
  const seenStatementIds = new Set<number>();
  const ordered: Array<Omit<CurrentStateVsmFlowStep, keyof StepTiming>> = [];

  const pushStep = (step: SequenceStepLike) => {
    if (step.statementId != null && seenStatementIds.has(step.statementId)) return;
    if (step.statementId != null) seenStatementIds.add(step.statementId);
    ordered.push({
      id: step.statementId == null ? `sequence-${ordered.length + 1}` : `statement-${step.statementId}`,
      order: ordered.length + 1,
      statementId: step.statementId,
      label: humanizeLabel(step.label || statementLabel(input.statementById, step.statementId)),
    });
  };

  for (const step of input.sequenceSteps) pushStep(step);

  if (input.vsmSteps.length > 0) {
    for (const [index, step] of input.vsmSteps.entries()) {
      const statementId = toNumber(step.statementId);
      if (statementId != null && seenStatementIds.has(statementId)) continue;
      if (statementId != null) seenStatementIds.add(statementId);
      ordered.push({
        id: statementId == null ? `vsm-step-${index + 1}` : `statement-${statementId}`,
        order: ordered.length + 1,
        statementId,
        label: humanizeLabel(statementLabel(input.statementById, statementId) || `Step ${index + 1}`),
      });
    }
  }

  if (ordered.length === 0) {
    for (const statement of input.parsedStatements) {
      ordered.push({
        id: `statement-${statement.id}`,
        order: ordered.length + 1,
        statementId: statement.id,
        label: humanizeLabel(statement.name),
      });
    }
  }

  if (ordered.length === 0) {
    for (const step of extractSpineSteps(input.spine)) pushStep(step);
  }

  return ordered;
}

function deriveStepTiming(step: VsmStepLike | undefined): StepTiming {
  return {
    workTime: parseDurationFact(step?.ct ?? (typeof step?.workTimeMinutes === 'number' ? `${step.workTimeMinutes}m` : undefined)),
    waitTime: parseDurationFact(step?.wt ?? (typeof step?.waitTimeMinutes === 'number' ? `${step.waitTimeMinutes}m` : undefined)),
    quality: parsePercentFact(step?.ca),
    waitReason: extractWaitReason(step),
  };
}

function deriveMetrics(flow: CurrentStateVsmFlowStep[]): CurrentStateVsmMetrics {
  if (flow.length === 0) return EMPTY_METRICS;

  const allWorkKnown = flow.every((step) => step.workTime.status === 'known');
  const allWaitKnown = flow.every((step) => step.waitTime.status === 'known');
  const allQualityKnown = flow.every((step) => step.quality.status === 'known');

  const workSeconds = sumKnownDurations(flow.map((step) => step.workTime));
  const waitSeconds = sumKnownDurations(flow.map((step) => step.waitTime));
  const leadSeconds = workSeconds + waitSeconds;

  return {
    leadTime:
      allWorkKnown && allWaitKnown
        ? knownDuration(leadSeconds)
        : missingDuration('Capture work and wait times for every step.'),
    workTime:
      allWorkKnown ? knownDuration(workSeconds) : missingDuration('Capture work time for every step.'),
    waitTime:
      allWaitKnown ? knownDuration(waitSeconds) : missingDuration('Capture wait time for every step.'),
    flowEfficiency:
      allWorkKnown && allWaitKnown && leadSeconds > 0
        ? knownPercent((workSeconds / leadSeconds) * 100)
        : missingPercent('Capture work and wait times to calculate flow efficiency.'),
    quality: allQualityKnown
      ? knownPercent(deriveRolledQuality(flow))
      : missingPercent('Capture first-time-right quality for every step.'),
  };
}

function deriveMissingFacts(flow: CurrentStateVsmFlowStep[]): CurrentStateVsmMissingFact[] {
  const facts: CurrentStateVsmMissingFact[] = [];

  for (const step of flow) {
    if (step.workTime.status === 'missing') {
      facts.push({
        kind: 'work-time',
        stepId: step.id,
        stepLabel: step.label,
        message: `Work time is missing for ${step.label}.`,
      });
    }
    if (step.waitTime.status === 'missing') {
      facts.push({
        kind: 'wait-time',
        stepId: step.id,
        stepLabel: step.label,
        message: `Wait time is missing for ${step.label}.`,
      });
    } else if (step.waitTime.seconds > 0 && !step.waitReason) {
      facts.push({
        kind: 'wait-reason',
        stepId: step.id,
        stepLabel: step.label,
        message: `Wait reason is missing for ${step.label}.`,
      });
    }
    if (step.quality.status === 'missing') {
      facts.push({
        kind: 'quality',
        stepId: step.id,
        stepLabel: step.label,
        message: `Quality is missing for ${step.label}.`,
      });
    }
  }

  return facts;
}

function deriveBottlenecks(
  flow: CurrentStateVsmFlowStep[],
  painLinks: unknown[] | undefined,
): CurrentStateVsmBottleneck[] {
  const candidates: CurrentStateVsmBottleneck[] = [];
  const waitSteps = flow.filter((step) => step.waitTime.status === 'known' && step.waitTime.seconds > 0);
  const longestWait = waitSteps.reduce<CurrentStateVsmFlowStep | null>((longest, step) => {
    if (!longest) return step;
    return step.waitTime.status === 'known'
      && longest.waitTime.status === 'known'
      && step.waitTime.seconds > longest.waitTime.seconds
      ? step
      : longest;
  }, null);

  if (longestWait?.waitTime.status === 'known') {
    candidates.push({
      kind: 'wait',
      stepId: longestWait.id,
      stepLabel: longestWait.label,
      reason: 'Longest wait in the flow',
      detail: `${longestWait.waitTime.display} waiting`,
    });
  }

  const longestElapsed = flow.reduce<{
    step: CurrentStateVsmFlowStep;
    seconds: number;
  } | null>((longest, step) => {
    if (step.workTime.status !== 'known' || step.waitTime.status !== 'known') return longest;
    const seconds = step.workTime.seconds + step.waitTime.seconds;
    if (!longest || seconds > longest.seconds) return { step, seconds };
    return longest;
  }, null);

  if (
    longestElapsed
    && longestElapsed.seconds > 0
    && longestElapsed.step.id !== longestWait?.id
  ) {
    candidates.push({
      kind: 'elapsed-time',
      stepId: longestElapsed.step.id,
      stepLabel: longestElapsed.step.label,
      reason: 'Longest total time',
      detail: `${formatDuration(longestElapsed.seconds)} from work and waiting`,
    });
  }

  for (const step of flow) {
    if (step.quality.status === 'known' && step.quality.percent < 95) {
      candidates.push({
        kind: 'quality',
        stepId: step.id,
        stepLabel: step.label,
        reason: 'Quality drop-off',
        detail: `${step.quality.display} first time right`,
      });
    }
  }

  for (const pain of painLinks ?? []) {
    const painStep = matchPainToStep(pain, flow);
    if (!painStep) continue;
    candidates.push({
      kind: 'pain',
      stepId: painStep.id,
      stepLabel: painStep.label,
      reason: 'Pain point linked here',
      detail: extractPainText(pain) || 'Captured as a pain point',
    });
  }

  return uniqueBottlenecks(candidates).slice(0, 3);
}

function selectProcess(vsm: VsmLike | null | undefined): VsmProcessLike | null {
  const processes = Array.isArray(vsm?.processes) ? vsm?.processes ?? [] : [];
  return (
    processes.find((process) => Array.isArray(process.steps) && process.steps.length > 0)
    ?? processes[0]
    ?? null
  );
}

function extractSequenceSteps(raw: unknown): SequenceStepLike[] {
  const source = Array.isArray(raw)
    ? raw
    : firstArrayProperty(raw, ['steps', 'sequence', 'items', 'orderedSteps']);

  return source
    .map((item, index) => ({
      order: toNumber(readProperty(item, ['order', 'position', 'index', 'sequence'])) ?? index + 1,
      statementId: toNumber(readProperty(item, ['statementId', 'statement', 'statementID', 'flowStatementId'])),
      label: readString(item, ['label', 'name', 'title', 'text']) || '',
    }))
    .filter((step) => step.statementId != null || step.label.trim().length > 0)
    .sort((a, b) => a.order - b.order);
}

function extractSpineSteps(raw: unknown[] | null | undefined): SequenceStepLike[] {
  const source = Array.isArray(raw) ? raw : [];
  return source
    .map((item, index) => ({
      order: toNumber(readProperty(item, ['order'])) ?? index + 1,
      statementId: toNumber(readProperty(item, ['statementId', 'statementID'])),
      label: readString(item, ['label', 'name', 'title', 'text']) || '',
    }))
    .filter((step) => step.label.trim().length > 0)
    .sort((a, b) => a.order - b.order);
}

function firstArrayProperty(raw: unknown, keys: string[]): unknown[] {
  if (!isRecord(raw)) return [];
  for (const key of keys) {
    const value = raw[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function parseDurationFact(raw: unknown): DurationFact {
  const parsed = parseDurationSeconds(raw);
  return parsed == null ? { status: 'missing', display: 'Missing' } : knownDuration(parsed);
}

function parsePercentFact(raw: unknown): PercentFact {
  const percent = parsePercent(raw);
  return percent == null ? { status: 'missing', display: 'Missing' } : knownPercent(percent);
}

function parseDurationSeconds(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw >= 0 ? raw : null;
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === '0') return 0;
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days)?$/);
  if (!match) return null;
  const value = Number.parseFloat(match[1]);
  if (!Number.isFinite(value) || value < 0) return null;
  switch (match[2]) {
    case 'd':
    case 'day':
    case 'days':
      return value * 86400;
    case 'h':
    case 'hr':
    case 'hrs':
    case 'hour':
    case 'hours':
      return value * 3600;
    case 'm':
    case 'min':
    case 'mins':
    case 'minute':
    case 'minutes':
      return value * 60;
    case 's':
    case 'sec':
    case 'secs':
    case 'second':
    case 'seconds':
    default:
      return value;
  }
}

function parsePercent(raw: unknown): number | null {
  if (raw == null) return null;
  const value =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number.parseFloat(raw.replace('%', '').trim())
        : Number.NaN;
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}

function knownDuration(seconds: number): KnownDurationMetric & KnownDurationFact {
  return {
    status: 'known',
    kind: 'duration',
    seconds,
    display: formatDuration(seconds),
  };
}

function knownPercent(percent: number): KnownPercentMetric & KnownPercentFact {
  const rounded = round1(percent);
  return {
    status: 'known',
    kind: 'percent',
    percent: rounded,
    display: `${trimDecimal(rounded)}%`,
  };
}

function missingDuration(reason: string): MissingDurationMetric {
  return {
    status: 'missing',
    kind: 'duration',
    display: 'Missing',
    reason,
  };
}

function missingPercent(reason: string): MissingPercentMetric {
  return {
    status: 'missing',
    kind: 'percent',
    display: 'Missing',
    reason,
  };
}

function sumKnownDurations(facts: DurationFact[]): number {
  return facts.reduce(
    (total, fact) => (fact.status === 'known' ? total + fact.seconds : total),
    0,
  );
}

function deriveRolledQuality(flow: CurrentStateVsmFlowStep[]): number {
  const rolled = flow.reduce((total, step) => {
    if (step.quality.status !== 'known') return total;
    return total * (step.quality.percent / 100);
  }, 1);
  return rolled * 100;
}

function deriveStatus(
  flow: CurrentStateVsmFlowStep[],
  missingFacts: CurrentStateVsmMissingFact[],
  hasBaseline: boolean,
): CurrentStateVsmStatus {
  if (flow.length === 0) return 'empty';
  if (hasBaseline) return 'frozen';
  return missingFacts.length > 0 ? 'partial' : 'ready';
}

function hasKnownMetric(metrics: CurrentStateVsmMetrics): boolean {
  return Object.values(metrics).some((metric) => metric.status === 'known');
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0';
  if (seconds >= 2 * 86400) return `${trimDecimal(seconds / 86400)}d`;
  if (seconds >= 3600) return `${trimDecimal(seconds / 3600)}h`;
  if (seconds >= 60) return `${trimDecimal(seconds / 60)}m`;
  return `${trimDecimal(seconds)}s`;
}

function trimDecimal(value: number): string {
  const rounded = round1(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function statementLabel(
  statementById: Map<number, FlowStatementLike>,
  statementId: number | null,
): string {
  return statementId == null ? '' : statementById.get(statementId)?.name ?? '';
}

function humanizeLabel(raw: string | undefined): string {
  const source = String(raw || '').trim();
  const preserveSentenceCase = /\s/.test(source);
  const cleaned = source
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return 'Captured step';
  if (preserveSentenceCase) return cleaned;
  return cleaned
    .split(' ')
    .map((word) => (word.toUpperCase() === word ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

function extractWaitReason(step: VsmStepLike | undefined): string | null {
  if (!step) return null;
  return (
    readString(step, ['waitReason', 'waitReasonText', 'waitCause', 'delayReason', 'reason', 'notes'])
    || null
  );
}

function matchPainToStep(
  pain: unknown,
  flow: CurrentStateVsmFlowStep[],
): CurrentStateVsmFlowStep | null {
  const statementId = toNumber(readProperty(pain, ['statementId', 'stepStatementId', 'flowStatementId']));
  if (statementId != null) {
    const matched = flow.find((step) => step.statementId === statementId);
    if (matched) return matched;
  }
  const label = readString(pain, ['stepLabel', 'label', 'step', 'name']);
  if (label) {
    const normalized = label.trim().toLowerCase();
    return flow.find((step) => step.label.trim().toLowerCase() === normalized) ?? null;
  }
  return null;
}

function extractPainText(pain: unknown): string | null {
  return readString(pain, ['text', 'description', 'evidence', 'summary', 'note']) || null;
}

function uniqueBottlenecks(
  candidates: CurrentStateVsmBottleneck[],
): CurrentStateVsmBottleneck[] {
  const seen = new Set<string>();
  const unique: CurrentStateVsmBottleneck[] = [];
  for (const candidate of candidates) {
    const key = `${candidate.kind}:${candidate.stepId ?? candidate.stepLabel}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(candidate);
  }
  return unique;
}

function getBaselineSessionId(raw: unknown): string | null {
  const sessionId = readString(raw, ['sessionId']);
  return sessionId || null;
}

function readString(raw: unknown, keys: string[]): string {
  const value = readProperty(raw, keys);
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function readProperty(raw: unknown, keys: string[]): unknown {
  if (!isRecord(raw)) return undefined;
  for (const key of keys) {
    if (key in raw) return raw[key];
  }
  return undefined;
}

function toNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === 'object' && raw !== null;
}
